import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Container,
  Grid,
  Paper,
  Button,
  Typography,
} from "@mui/material";
import { Oval } from "react-loader-spinner";
import Toolbar from '../components/Toolbar'
import CommonTools from "../components/SQLGenerator/CommonTools";
import CustomTreeView from "../components/SQLGenerator/Tree/CustomTreeView";
import MainView from "../components/SQLGenerator/MainView";
import PropertyView from "../components/SQLGenerator/Property";
import Result from "../components/SQLGenerator/sheet";
import Modal from "../components/SQLGenerator/Dialogs/Modal";
import AddTableDialog from "../components/SQLGenerator/Dialogs/AddTableDialog";
import NewConnection from "../components/SQLGenerator/Dialogs/NewConnection";
import ParameterDialog from "../components/SQLGenerator/Dialogs/ParameterDialog";
import CreateViewDialog from "../components/SQLGenerator/Dialogs/CreateViewDailog";
import { getTables, addUpdateItem } from "../slices/table";
import { initAllState } from "../slices/query";
import { initAllUtility } from "../slices/utility";
import { initAllTable } from "../slices/table";
import { connectDB } from "../slices/database";
import { runQuery } from "../slices/query";
import { setTreeOpened, setSheetOpened } from "../slices/utility";
import TableService from "../services/TableService";
import { useCustomSnackbar } from "../CustomSnackbarProvider";
import { notifyContents } from "../components/SQLGenerator/common/Notification";

import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  color: theme.palette.text.secondary,
}));

const useStyles = makeStyles()((theme) => {
  return {
    boxStyle: {
      width: "100%",
      padding: 0,
    },
    treeBoxStyle: {
      borderBottom: "1px solid #ddd",
    },
    treeHeight: {
      height: "calc(100% - 62px)",
      overflow: "auto",
    },
    loadingBox: {
      position: "absolute",
      width: "100%",
      height: "100%",
      background: "#50657a1a",
      zIndex: 10000,
    },
    loadingContent: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    },
  };
});

const useStyleButton = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  startIcon: {
    marginBottom: 4,
  },
});
const SQLGenerator = () => {
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const operatorType = useSelector((state) => state.query.operatorType);
  const isConnected = useSelector((state) => state.database.success);
  const isTreeOpened = useSelector((state) => state.utility.treeOpened);
  const queryData = useSelector((state) => state.query);
  const joinCommand = useSelector((state) => state.query.joinCommand);
  const isCrossTab = useSelector((state) => state.utility.isCrossTab);
  const crosstabSelectors = useSelector(
    (state) => state.query.crosstabSelectorFields
  );
  const crosstabColumns = useSelector(
    (state) => state.query.crosstabColumnFields
  );
  const crosstabValues = useSelector(
    (state) => state.query.crosstabValueFields
  );
  const edited = useSelector((state) => state.utility.edited);
  const codeSQL = useSelector((state) => state.utility.codeSQL);
  const parameters = useSelector((state) => state.utility.parameters);
  const isUnique = useSelector((state) => state.utility.isUnique);
  const uniqueTable = useSelector((state) => state.utility.uniqueTable);
  const dbInformation = useSelector((state) => state.database.dbInfo);

  const [isLoading, setIsLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [addDialog, setAddDialog] = React.useState(false);
  const [paramDialog, setParamDialog] = React.useState(false);
  const [createViewDialog, setCreateViewDialog] = React.useState(false);
  const [paramDialogForView, setParamDialogForView] = React.useState(false);

  const { buttonStyle } = useStyleButton();

  const snackbarWithStyle = useCustomSnackbar();
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setIsLoading(false);
  };

  const handleClickAddTables = () => {
    setAddDialog(true);
  };

  const handleShowCreateView = () => {
    setParamDialogForView(false);
    setCreateViewDialog(true);
  };
  const handleAddTableClose = () => {
    setAddDialog(false);
    dispatch(setTreeOpened(true));
  };
  const handleTableClose = () => {
    setAddDialog(false);
  };
  const handleParamClose = () => {
    setParamDialog(false);
    setParamDialogForView(false);
  };

  const handleCreateView = () => {
    if (parameters.length > 0 && !edited) {
      setParamDialogForView(true);
    } else {
      setCreateViewDialog(true);
    }
  };
  const handleCreateViewClose = () => {
    setCreateViewDialog(false);
  };
  const handleConnect = (dbInfos) => {
    setIsLoading(true);
    dispatch(connectDB(dbInfos))
      .unwrap()
      .then((data) => {
        if (data.success) {
          setAddDialog(false);
          snackbarWithStyle(notifyContents.newConnectSuccess, "success");
          setOpen(false);
          setSuccess(true);
        } else {
          snackbarWithStyle(notifyContents.newConnectFail, "error");
          setSuccess(false);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        snackbarWithStyle(notifyContents.newConnectFail, "error");
        setIsLoading(false);
      });
  };

  const handleReset = (event) => {
    dispatch(getTables());
    dispatch(initAllState());
    dispatch(initAllUtility());
    dispatch(initAllTable());
    snackbarWithStyle(notifyContents.resetSuccess, "success");
  };
  const handleRunQuery = (event) => {
    if (parameters.length > 0 && !edited) {
      setParamDialog(true);
    } else {
      let query = GetQueryBasedOnParam();
      const queryInfo = {
        query: query,
      };

      setIsLoading(true);
      dispatch(runQuery(queryInfo))
        .then((data) => {
          dispatch(setSheetOpened(true));
          setIsLoading(false);
          // check if run query is success or failed.
          if (data.hasOwnProperty("error")) {
            snackbarWithStyle(notifyContents.runQueryFail, "error");
          } else {
            snackbarWithStyle(notifyContents.runQuerySuccess, "success");
          }
        })
        .catch((err) => {
          console.log(err);
          setIsLoading(false);
          snackbarWithStyle(notifyContents.runQueryFail, "error");

          setOpenModal(true);
        });
    }
  };

  const SaveView = (viewName) => {
    const queryCommand = GetQueryBasedOnParam();
    console.log(queryCommand);
    let query = `CREATE VIEW ${viewName} AS ${queryCommand}`;
    const queryInfo = {
      query: query,
    };

    dispatch(runQuery(queryInfo))
      .then(async (data) => {
        setCreateViewDialog(false);
        const res = await TableService.getTables();
        dispatch(addUpdateItem({ newArray: res.data }));
      })
      .catch();
  };

  const GetQueryBasedOnParam = () => {
    const selectFields = queryData.selectFields;
    let fromTable = "",
      joinFields = [],
      sortFields = [],
      filterFields = [],
      joinArray;

    let tableNameArray = [];
    selectFields.map((item) => {
      const {
        data: { table },
      } = item;
      tableNameArray.push(table);
    });
    const uniqueArray1 = [
      ...new Set(tableNameArray.filter((item) => item !== "None")),
    ];
    let modifiedTable = uniqueArray1.join(",");
    modifiedTable = modifiedTable.replace("None", "");
    modifiedTable = modifiedTable.replace(/^,|,$/g, "");
    fromTable = `FROM ${modifiedTable}`;

    queryData.relationFields.forEach((item, index) => {
      if (item.LTable.length > 0) {
        if (index === 0) fromTable = `FROM ${item.LTable[0]}`;
        joinFields.push({
          joinTable: item.RTable[0],
          LFields: item.LFields,
          RFields: item.RFields,
          Operators: item.Operator,
          joinType: item.joinType,
        });
      }
    });
    queryData.sortFields.forEach((item, index) => {
      if (index !== queryData.sortFields.length - 1) {
        const {
          data: { field, table, sortType },
        } = item;
        sortFields.push({
          field,
          table,
          sortType,
        });
      }
    });

    const typeAry1 = ["=", "!=", ">", ">=", "<", "<="];
    const typeAry2 = ["=", "!=", "Like", "Not Like"];

    queryData.filterFields.forEach((item, index) => {
      if (index !== queryData.filterFields.length - 1) {
        const { filterVariant, filterValue, operator, type } = item;
        const {
          data: { table, field },
        } = filterVariant[0];
        let typeOperator = typeAry1[operator];
        if (type === "Text") typeOperator = typeAry2[operator];
        if (filterValue.isParam) {
          filterFields.push({
            table,
            field,
            typeOperator,
            value: "@" + filterValue.parameter,
            type,
          });
        } else {
          filterFields.push({
            table,
            field,
            typeOperator,
            value: filterValue.default,
            type,
          });
        }
      }
    });

    console.log(joinCommand);
    joinArray = joinCommand.match(/\bAND\b|\bOR\b/g);
    joinArray = joinCommand.match(/\bAND\b|\bOR\b/g);
    console.log(joinArray);
    let selectQuery = "SELECT ";

    selectFields.map((item, index) => {
      const {
        data: { table, field, header_name, aggreType, command, dropbox },
      } = item;
      let prev_name = `${table}.${field}`;
      if (command == null && command == undefined) {
        if (aggreType !== "none") prev_name = `${aggreType}( ${prev_name} )`;
        if (header_name !== field)
          selectQuery += `${prev_name} as "${header_name}" `;
        if (header_name === field) selectQuery += `${prev_name}`;
        if (index !== selectFields.length - 1) selectQuery += ", \n";
      } else {
        if (command != null) {
          let updatedString = command.replace(/{\d+}/g, (match) => {
            const index = parseInt(match.match(/\d+/)[0]);
            return (
              dropbox[index - 1].filterVariant[0].data.table +
              "." +
              dropbox[index - 1].filterVariant[0].data.field
            );
          });
          let regex = /DAY\((.*?)\)/g;
          let matchResult;

          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedValue = matchResult[1];
            updatedString = updatedString.replace(
              matchResult[0],
              `EXTRACT(DAY FROM ${extractedValue})`
            );
          }

          matchResult = updatedString.match(/DATE_ADD\((.*)\)/);
          if (matchResult && matchResult[1]) {
            let extractedElements = matchResult[1].split(",");
            if (
              extractedElements[0] === "'second'" ||
              extractedElements[0] === "'minute'" ||
              extractedElements[0] === "'hour'" ||
              extractedElements[0] === "'day'" ||
              extractedElements[0] === "'week'" ||
              extractedElements[0] === "'month'" ||
              extractedElements[0] === "'quarter'" ||
              extractedElements[0] === "'year'" ||
              extractedElements[0] === "'millisecond'"
            ) {
              const isDate = !isNaN(Date.parse(extractedElements[2]));
              if (isDate && extractedElements[0] !== "'millisecond'")
                extractedElements[2] = "DATE " + extractedElements[2];
              if (isDate && extractedElements[0] === "'millisecond'")
                extractedElements[2] = "TIMESTAMP " + extractedElements[2];
              if (
                extractedElements[0] !== "'year'" &&
                extractedElements[0] !== "'month'" &&
                extractedElements[0] !== "'quarter'" &&
                extractedElements[0] !== "'week'"
              )
                extractedElements[0] = `${extractedElements[0].replace(
                  /'/g,
                  ""
                )}s`;
              else
                extractedElements[0] = `${extractedElements[0].replace(
                  /'/g,
                  ""
                )}`;
              updatedString = updatedString.replace(
                /DATE_ADD\((.*)\)/,
                `${extractedElements[2]} + INTERVAL '${extractedElements[1]} ${extractedElements[0]}'`
              );
            }
          }

          // matchResult = updatedString.match(/DATE_DIFF\((.*?)\)/)
          // if(matchResult && matchResult[1]) {
          //   let extractedElements = matchResult[1].split(',');
          //   updatedString = updatedString.replace(/DATE_DIFF\((.*)\)/, `DATE_PART(${extractedElements[0]}, AGE(${extractedElements[1]},${extractedElements[2]}))`);
          // }

          regex = /DATE_DIFF\((.*?)\)/g;
          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedElements = matchResult[1].split(",");
            updatedString = updatedString.replace(
              matchResult[0],
              `DATE_PART(${extractedElements[0]}, AGE(${extractedElements[1]},${extractedElements[2]}))`
            );
          }

          const formatStringMap = {
            "%a": "Dy",
            "%b": "Mon",
            "%c": "MM",
            "%d": "DD",
            "%e": "FMDD",
            "%f": "MS",
            "%H": "HH24",
            "%h": "HH12",
            "%I": "HH12",
            "%i": "MI",
            "%j": "DDD",
            "%k": "HH24",
            "%l": "HH12",
            "%M": "Month",
            "%m": "MM",
            "%p": "AM",
            "%r": "HH:MI:SSam",
            "%s": "SS",
            "%S": "SS",
            "%T": "HH24:MI:SS",
            "%v": "WW",
            "%W": "Day",
            "%x": "IYYY",
            "%Y": "YYYY",
            "%y": "YY",
            "%%": "%%",
          };

          regex = /DATE_FORMAT\((.*?)\)/g;

          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedElements = matchResult[1].split(",");
            extractedElements[1] = extractedElements[1].replace(/'/g, "");
            const newFormat = formatStringMap[extractedElements[1]];
            updatedString = updatedString.replace(
              matchResult[0],
              `TO_CHAR(${extractedElements[0]}::DATE, '${newFormat}')`
            );
          }

          regex = /EOMONTH\((.*?)\)/g;

          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedElements = matchResult[1].split(",");
            extractedElements[1] = extractedElements[1].replace(/'/g, "");
            updatedString = updatedString.replace(
              matchResult[0],
              `DATE_TRUNC('MONTH', ${extractedElements[0]}::date) + INTERVAL '1 MONTH - 1 DAY' * ${extractedElements[1]}`
            );
          }

          regex = /MONTH\((.*?)\)/g;

          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedValue = matchResult[1];
            updatedString = updatedString.replace(
              matchResult[0],
              `EXTRACT(MONTH FROM ${extractedValue})`
            );
          }

          regex = /YEAR\((.*?)\)/g;

          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedValue = matchResult[1];
            updatedString = updatedString.replace(
              matchResult[0],
              `EXTRACT(YEAR FROM ${extractedValue})`
            );
          }

          regex = /QUARTER\((.*?)\)/g;

          while ((matchResult = regex.exec(updatedString)) !== null) {
            const extractedValue = matchResult[1];
            updatedString = updatedString.replace(
              matchResult[0],
              `EXTRACT(QUARTER FROM ${extractedValue})`
            );
          }

          if (updatedString === "") updatedString = null;
          updatedString += " as ";
          updatedString += `"${header_name}"`;
          if (index !== selectFields.length - 1)
            selectQuery += updatedString + ", \n";
          else selectQuery += updatedString;
        }
      }
    });

    let groupByQuery = "GROUP BY";
    let isGroupBy = 0;

    selectFields.map((item, index) => {
      const {
        data: { aggreType },
      } = item;
      if (aggreType !== "none") isGroupBy = 1;
    });

    if (isGroupBy) {
      let cnt = 0;
      selectFields.map((item, index) => {
        const {
          data: { table, field, aggreType },
        } = item;
        if (aggreType == "none" && field !== "None") {
          let string = " " + table + "." + field;
          groupByQuery += string;
          groupByQuery += ",";
          cnt++;
        }
      });
      groupByQuery = groupByQuery.slice(0, -1);
      if (cnt === 0) isGroupBy = 0;
    }

    if (isCrossTab) {
      selectQuery = "SELECT ";
      crosstabSelectors.map((item, index) => {
        const {
          data: { table, field },
        } = item;
        selectQuery += `${table}.${field}`;
        selectQuery += ", \n";
        if (crosstabValues[0].id !== "none") {
          let string = " " + table + "." + field;
          if (index < crosstabSelectors.length) groupByQuery += string;
          if (index < crosstabSelectors.length - 1) groupByQuery += ",";
          if (
            index == crosstabSelectors.length - 1 &&
            crosstabValues[0].id !== "none"
          )
            groupByQuery += ",";
        }
      });

      if (crosstabColumns[0].id !== "none") {
        const {
          data: { table, field },
        } = crosstabColumns[0];
        selectQuery += `${table}.${field}`;
        selectQuery += ", \n";
        if (crosstabValues[0].id !== "none") {
          let string = " " + table + "." + field;
          groupByQuery += string;
        }
      }

      if (crosstabValues[0].id !== "none") {
        const {
          data: { table, field },
        } = crosstabValues[0];
        selectQuery += `${operatorType.changed}(${table}.${field})`;
      }
    }

    let joinQuery = "";
    const joinTypeArray = [
      "LEFT JOIN",
      "RIGHT JOIN",
      "INNER JOIN",
      "FULL JOIN",
    ];
    const OperatorTypeArray = ["=", "!=", ">", ">=", "<", "<="];

    joinFields.map((item) => {
      const { joinTable, LFields, RFields, Operators, joinType } = item;
      let joinString = `${joinTypeArray[joinType]} ${joinTable} ON`;
      for (let i = 0; i < LFields.length; i++) {
        joinString += ` ${LFields[i]}${OperatorTypeArray[Operators[i]]}${
          RFields[i]
        }`;
        if (i !== LFields.length - 1) {
          joinString += " AND";
        }
      }
      joinQuery += `${joinString}\n`;
    });

    let sortQuery = "ORDER BY";

    sortFields.forEach((item, index) => {
      sortQuery += ` ${item.table}.${item.field} ${item.sortType}`;
      if (index !== sortFields.length - 1) sortQuery += ",";
    });

    let whereQuery = "WHERE ";

    console.log(joinArray);
    filterFields.map((item, index) => {
      if (index > 0) {
        whereQuery += `\n      ${joinArray[index - 1]} `;
      }

      let valueString = item.value;
      if (item.type === "Text") valueString = `'${item.value}'`;
      let condition = "";
      if (item.typeOperator === "Like" || item.typeOperator === "Not Like") {
        condition =
          item.table +
          "." +
          item.field +
          " " +
          item.typeOperator.toUpperCase() +
          " " +
          `'%${item.value}%'`;
      } else
        condition =
          item.table + "." + item.field + item.typeOperator + valueString;
      whereQuery += condition;
    });

    let query = "";
    let cSelectQuery = "";

    if (selectFields.length > 0) {
      query += selectQuery + "\n";
    }

    if (fromTable) query += fromTable + "\n";
    if (joinFields.length > 0) query += joinQuery;
    if (filterFields.length > 0) query += whereQuery + "\n";
    if (isGroupBy) query += groupByQuery + "\n";
    if (!isCrossTab && sortFields.length > 0) query += sortQuery + "\n";
    if (isCrossTab && crosstabValues[0].id !== "none")
      query += groupByQuery + "\n";

    if (isCrossTab) {
      if (crosstabColumns[0].id !== "none") {
        const {
          data: { table, field },
        } = crosstabColumns[0];
        cSelectQuery = `SELECT DISTINCT ${field} FROM ${table} ORDER BY 1`;
      }

      const queryInfo = {
        query: cSelectQuery,
      };

      dispatch(runQuery(queryInfo))
        .then((data) => {
          let newParam = "";
          crosstabSelectors.map((item, index) => {
            const {
              data: { table, field },
            } = item;
            let type = "text";
            newParam += `${field} ${type},`;
          });

          let selectQuery = newParam;
          data.payload.rows.forEach((item, index) => {
            const key = Object.keys(item)[0];
            const value = item[key].replaceAll(" ", "_");
            let type = "text";
            if (typeof value == "string") type = "text";
            selectQuery += `${value} ${type}`;
            if (index !== data.payload.rows.length - 1) selectQuery += ",";
          });
          let crosstabQuery = `SELECT * FROM crosstab('${query}', '${cSelectQuery}') AS virtualTable (${selectQuery});`;
          query = crosstabQuery;
        })
        .catch((err) => {
          console.log(err);
        });
    } else {
      // setDefaultList(query);
    }
    if (edited || isCrossTab) query = codeSQL;

    queryData.filterFields.forEach((item, index) => {
      if (index !== queryData.filterFields.length - 1) {
        const { filterVariant, filterValue, operator, type } = item;
        const {
          data: { table, field },
        } = filterVariant[0];
        let typeOperator = typeAry1[operator];
        if (type === "Text") typeOperator = typeAry2[operator];
        let filterText = "";
        if (filterValue.isParam) {
          const parameterName = filterValue.parameter;
          parameters.forEach((item) => {
            if (item.name === parameterName) {
              filterText = item.value;

              query = query.replace("@" + filterValue.parameter, filterText);
            }
          });
        } else filterText = filterValue.default;
        filterFields.push({
          table,
          field,
          typeOperator,
          value: filterText,
          type,
        });
      }
    });

    console.log(query);

    return query;
  };
  const handleRun = () => {
    const query = GetQueryBasedOnParam();
    const queryInfo = {
      query: query,
    };

    setIsLoading(true);
    dispatch(runQuery(queryInfo))
      .then((data) => {
        dispatch(setSheetOpened(true));
        setIsLoading(false);
        // check if run query is success or failed.
        if (data.hasOwnProperty("error")) {
          snackbarWithStyle(notifyContents.runQueryFail, "error");
        } else {
          snackbarWithStyle(notifyContents.runQuerySuccess, "success");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setOpenModal(true);
        snackbarWithStyle(notifyContents.runQueryFail, "error");
      });
    setParamDialog(false);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {isLoading && (
        <div className={classes.loadingBox}>
          <div className={classes.loadingContent}>
            <Oval
              height={50}
              width={50}
              color="#2761c7"
              wrapperStyle={{}}
              wrapperClass=""
              visible={true}
              ariaLabel="oval-loading"
              secondaryColor="#2761c7"
              strokeWidth={3}
              strokeWidthSecondary={3}
            />
          </div>
        </div>
      )}

      <Box component="main" className={classes.boxStyle}>
        <Toolbar/>
        <CommonTools />
        <Grid container spacing={2} sx={{ mt: 2, px: 2 }}>
          <Grid item size={3}>
            <Paper sx={{ height: 600, p: 2 }}>
              <Box className={classes.treeBoxStyle}>
                <Typography>Source</Typography>
                <Button
                  color="inherit"
                  onClick={handleClickAddTables}
                  disabled={!isConnected}
                >
                  Add table data
                </Button>
              </Box>
              <Box className={classes.treeHeight} sx={{ mt: 1 }}>
                {isTreeOpened && <CustomTreeView />}
              </Box>
            </Paper>
          </Grid>

          <Grid item size={6}>
            <Paper sx={{ height: 600, p: 2 }}>
              <MainView />
            </Paper>
          </Grid>

          <Grid item size={3}>
            <Paper sx={{ height: 600, p: 2 }}>
              <PropertyView />
            </Paper>
          </Grid>
        </Grid>
        <Result sx={{position: 'fixed', bottom: 0}}/>
      </Box>
      <NewConnection
        open={open}
        handleClose={handleClose}
        handleConnect={handleConnect}
      />
      <Modal
        open={openModal}
        param={{ title: "Failed", content: "An error occured!" }}
        handleClose={handleCloseModal}
      />
      <AddTableDialog
        open={addDialog}
        handleAddTableClose={handleAddTableClose}
        handleTableClose={handleTableClose}
      />
      <ParameterDialog
        open={paramDialog}
        handleParamClose={handleParamClose}
        flag={1}
        handleRun={handleRun}
        handleShowCreateView={handleShowCreateView}
      />
      <ParameterDialog
        open={paramDialogForView}
        handleParamClose={handleParamClose}
        flag={2}
        handleRun={handleRun}
        handleShowCreateView={handleShowCreateView}
      />
      <CreateViewDialog
        open={createViewDialog}
        handleCreateViewClose={handleCreateViewClose}
        SaveView={SaveView}
      />
    </Box>
  );
};

export default SQLGenerator;
